import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { transcriptionService, TranscriptionSegment, TranscriptionSession } from '../services/TranscriptionService';
import { VoiceActivityDetector, VADEvent, VADStats } from '../services/VoiceActivityDetection';
import { FilteredSegment, FilteringStats } from '../services/ContentFilteringService';
import { useToast } from './Toast';

interface AudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob, filteredBlob: Blob, metadata: RecordingMetadata, transcription?: TranscriptionSession) => void;
  onRecordingStart?: () => void;
  onRecordingStop?: () => void;
  onTranscriptionUpdate?: (segment: TranscriptionSegment) => void;
  onSessionUpdate?: (session: TranscriptionSession) => void;
  onVADEvent?: (event: VADEvent) => void;
  onContentFiltered?: (filteredSegments: FilteredSegment[], stats: FilteringStats) => void;
  sessionId?: string;
  patientId?: string;
  appointmentReason?: string;
  sessionType?: string;
  isActive: boolean;
  autoStart?: boolean;
  enableTranscription?: boolean;
  enableVAD?: boolean;
  enableContentFiltering?: boolean;
}

interface RecordingMetadata {
  sessionId?: string;
  patientId?: string;
  startTime: Date;
  endTime?: Date;
  duration: number;
  sampleRate: number;
  channels: number;
  fileSize: number;
  filteredFileSize: number;
  silenceRemoved: number; // percentage of silence removed
  noiseReduction: boolean;
}

interface AudioFilterSettings {
  enableSilenceRemoval: boolean;
  silenceThreshold: number; // -60 dB default
  enableNoiseReduction: boolean;
  noiseGate: number; // -40 dB default
  enableNormalization: boolean;
}

export interface AudioRecorderHandle {
  stopRecording: () => void;
}

const AudioRecorder = forwardRef<AudioRecorderHandle, AudioRecorderProps>(({
  onRecordingComplete,
  onRecordingStart,
  onRecordingStop,
  onTranscriptionUpdate,
  onSessionUpdate,
  onVADEvent,
  onContentFiltered,
  sessionId,
  patientId,
  appointmentReason,
  sessionType,
  isActive,
  autoStart = false,
  enableTranscription = true,
  enableVAD = true,
  enableContentFiltering = true
}, ref) => {
  const toast = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [filterSettings, setFilterSettings] = useState<AudioFilterSettings>({
    enableSilenceRemoval: true,
    silenceThreshold: -60,
    enableNoiseReduction: true,
    noiseGate: -40,
    enableNormalization: true
  });

  // Transcription and VAD state
  const [currentTranscript, setCurrentTranscript] = useState<string>('');
  const [fullTranscript, setFullTranscript] = useState<string>('');
  const [transcriptionSession, setTranscriptionSession] = useState<TranscriptionSession | null>(null);
  const [vadStats, setVadStats] = useState<VADStats | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [speechActive, setSpeechActive] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);

  // Content filtering state
  const [filteringStats, setFilteringStats] = useState<FilteringStats | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const rawAudioDataRef = useRef<Float32Array[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const workletRef = useRef<AudioWorkletNode | null>(null);
  const vadRef = useRef<VoiceActivityDetector | null>(null);

  // Auto-start recording when session becomes active
  useEffect(() => {
    if (autoStart && isActive && !isRecording) {
      // Small delay to let the component fully mount and mic permissions settle
      const timer = setTimeout(() => {
        startRecording();
      }, 500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, isActive]);

  // Expose stopRecording to parent via ref
  useImperativeHandle(ref, () => ({
    stopRecording,
  }));

  // Audio level monitoring
  useEffect(() => {
    if (isRecording && !isPaused && analyserRef.current) {
      const updateAudioLevel = () => {
        const dataArray = new Uint8Array(analyserRef.current!.frequencyBinCount);
        analyserRef.current!.getByteFrequencyData(dataArray);

        // Calculate RMS level
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / dataArray.length);
        setAudioLevel(rms / 255);

        if (isRecording && !isPaused) {
          requestAnimationFrame(updateAudioLevel);
        }
      };
      updateAudioLevel();
    }
  }, [isRecording, isPaused]);

  // Timer for recording duration
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, isPaused]);

  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        }
      });

      streamRef.current = stream;
      startTimeRef.current = new Date();

      // Create AudioContext for real-time processing
      audioContextRef.current = new AudioContext({ sampleRate: 44100 });
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);

      analyserRef.current.fftSize = 2048;
      sourceRef.current.connect(analyserRef.current);

      // Set up audio worklet for raw audio capture (for filtering)
      try {
        await audioContextRef.current.audioWorklet.addModule('/audio-processor.js');
        workletRef.current = new AudioWorkletNode(audioContextRef.current, 'audio-processor');
        sourceRef.current.connect(workletRef.current);

        // Collect raw audio data for filtering
        workletRef.current.port.onmessage = (event) => {
          const audioData = event.data;
          rawAudioDataRef.current.push(new Float32Array(audioData));
        };
      } catch (error) {
        console.warn('Audio worklet not available, using basic recording:', error);
      }

      // Set up MediaRecorder for basic recording
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      chunksRef.current = [];
      rawAudioDataRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const filteredBlob = await processAudioWithFilters(rawAudioDataRef.current);

        // Stop transcription and get final session with filtering
        let finalTranscription: TranscriptionSession | null = null;
        if (enableTranscription && isTranscribing) {
          finalTranscription = await transcriptionService.stopTranscription();
          setIsTranscribing(false);

          // Get filtering results if content filtering was enabled
          if (enableContentFiltering && finalTranscription?.filteredSegments) {
            const stats = finalTranscription.filteringStats;
            setFilteringStats(stats);
            onContentFiltered?.(finalTranscription.filteredSegments, stats);
          }
        }

        // Stop VAD
        if (enableVAD && vadRef.current) {
          vadRef.current.stop();
        }

        const metadata: RecordingMetadata = {
          sessionId,
          patientId,
          startTime: startTimeRef.current!,
          endTime: new Date(),
          duration: recordingTime,
          sampleRate: audioContextRef.current?.sampleRate || 44100,
          channels: 1,
          fileSize: audioBlob.size,
          filteredFileSize: filteredBlob.size,
          silenceRemoved: calculateSilenceRemoved(rawAudioDataRef.current),
          noiseReduction: filterSettings.enableNoiseReduction
        };

        onRecordingComplete(audioBlob, filteredBlob, metadata, finalTranscription || undefined);
      };

      mediaRecorderRef.current.start(1000); // Capture data every second
      setIsRecording(true);
      setRecordingTime(0);

      // Start transcription with session context
      if (enableTranscription) {
        // Set up content filtering if enabled
        if (enableContentFiltering) {
          transcriptionService.setContentFilteringEnabled(true, 0.6);
        }

        const transcriptionStarted = await transcriptionService.startTranscription(
          sessionId || `session_${Date.now()}`,
          {
            onTranscript: (segment) => {
              setCurrentTranscript(segment.text);
              setTranscriptError(null);

              // Update full transcript for continuous display
              if (segment.isFinal && segment.text.trim()) {
                setFullTranscript(prev => prev + (prev ? ' ' : '') + segment.text.trim());
              }

              onTranscriptionUpdate?.(segment);
            },
            onSessionUpdate: (session) => {
              setTranscriptionSession(session);

              // Update full transcript from session
              if (session.fullText && session.fullText !== fullTranscript) {
                setFullTranscript(session.fullText);
              }

              // Update filtering stats in real-time if available
              if (enableContentFiltering && session.filteringStats) {
                setFilteringStats(session.filteringStats);
              }

              // Call parent's callback
              onSessionUpdate?.(session);
            },
            onError: (error) => {
              console.error('Transcription error:', error);
              setTranscriptError(error);
            }
          },
          {
            patientId,
            sessionType,
            appointmentReason
          }
        );
        setIsTranscribing(transcriptionStarted);
      }

      // Start VAD
      if (enableVAD) {
        vadRef.current = new VoiceActivityDetector();
        const vadInitialized = await vadRef.current.initialize(stream);

        if (vadInitialized) {
          vadRef.current.start({
            onVADEvent: (event) => {
              setSpeechActive(event.type === 'speech_start');
              onVADEvent?.(event);
            },
            onStatsUpdate: (stats) => {
              setVadStats(stats);
            }
          });
        }
      }

      onRecordingStart?.();

    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Microphone Error', 'Failed to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      // Clean up transcription
      if (isTranscribing) {
        transcriptionService.stopTranscription();
        setIsTranscribing(false);
        setCurrentTranscript('');
        setTranscriptError(null);
      }

      // Clean up VAD
      if (vadRef.current) {
        vadRef.current.cleanup();
        vadRef.current = null;
        setSpeechActive(false);
        setVadStats(null);
      }

      // Clean up resources
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      onRecordingStop?.();
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  // Audio filtering functions
  const processAudioWithFilters = async (audioData: Float32Array[]): Promise<Blob> => {
    if (audioData.length === 0) {
      return new Blob([], { type: 'audio/wav' });
    }

    // Concatenate all audio chunks
    const totalLength = audioData.reduce((sum, chunk) => sum + chunk.length, 0);
    const combinedAudio = new Float32Array(totalLength);
    let offset = 0;

    audioData.forEach(chunk => {
      combinedAudio.set(chunk, offset);
      offset += chunk.length;
    });

    let processedAudio = combinedAudio;

    // Apply filters
    if (filterSettings.enableSilenceRemoval) {
      processedAudio = removeSilence(processedAudio, filterSettings.silenceThreshold);
    }

    if (filterSettings.enableNoiseReduction) {
      processedAudio = reduceNoise(processedAudio, filterSettings.noiseGate);
    }

    if (filterSettings.enableNormalization) {
      processedAudio = normalizeAudio(processedAudio);
    }

    // Convert to WAV blob
    return createWavBlob(processedAudio, audioContextRef.current?.sampleRate || 44100);
  };

  const removeSilence = (audioData: Float32Array, threshold: number): Float32Array => {
    const thresholdLinear = Math.pow(10, threshold / 20); // Convert dB to linear
    const frameSize = 1024;
    const frames: Float32Array[] = [];

    for (let i = 0; i < audioData.length; i += frameSize) {
      const frame = audioData.slice(i, i + frameSize);

      // Calculate RMS of frame
      let rms = 0;
      for (let j = 0; j < frame.length; j++) {
        rms += frame[j] * frame[j];
      }
      rms = Math.sqrt(rms / frame.length);

      // Keep frame if above threshold
      if (rms > thresholdLinear) {
        frames.push(frame);
      }
    }

    // Concatenate kept frames
    const totalLength = frames.reduce((sum, frame) => sum + frame.length, 0);
    const result = new Float32Array(totalLength);
    let offset = 0;

    frames.forEach(frame => {
      result.set(frame, offset);
      offset += frame.length;
    });

    return result;
  };

  const reduceNoise = (audioData: Float32Array, noiseGate: number): Float32Array => {
    const gateLinear = Math.pow(10, noiseGate / 20);
    const result = new Float32Array(audioData.length);

    for (let i = 0; i < audioData.length; i++) {
      const amplitude = Math.abs(audioData[i]);
      if (amplitude > gateLinear) {
        result[i] = audioData[i];
      } else {
        result[i] = audioData[i] * 0.1; // Reduce noise by 90%
      }
    }

    return result;
  };

  const normalizeAudio = (audioData: Float32Array): Float32Array => {
    let maxAmplitude = 0;
    for (let i = 0; i < audioData.length; i++) {
      maxAmplitude = Math.max(maxAmplitude, Math.abs(audioData[i]));
    }

    if (maxAmplitude === 0) return audioData;

    const normalizeRatio = 0.95 / maxAmplitude;
    const result = new Float32Array(audioData.length);

    for (let i = 0; i < audioData.length; i++) {
      result[i] = audioData[i] * normalizeRatio;
    }

    return result;
  };

  const calculateSilenceRemoved = (audioData: Float32Array[]): number => {
    if (audioData.length === 0) return 0;

    const totalSamples = audioData.reduce((sum, chunk) => sum + chunk.length, 0);
    const thresholdLinear = Math.pow(10, filterSettings.silenceThreshold / 20);
    let silentSamples = 0;

    audioData.forEach(chunk => {
      for (let i = 0; i < chunk.length; i++) {
        if (Math.abs(chunk[i]) <= thresholdLinear) {
          silentSamples++;
        }
      }
    });

    return Math.round((silentSamples / totalSamples) * 100);
  };

  const createWavBlob = (audioData: Float32Array, sampleRate: number): Blob => {
    const length = audioData.length;
    const arrayBuffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(arrayBuffer);

    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length * 2, true);

    // Convert float samples to 16-bit PCM
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, audioData[i]));
      view.setInt16(offset, sample * 0x7FFF, true);
      offset += 2;
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  // Test transcription without recording
  const testTranscription = async () => {
    if (!enableTranscription) {
      toast.warning('Transcription Disabled', 'Transcription is currently disabled.');
      return;
    }

    try {
      // Set up content filtering if enabled
      if (enableContentFiltering) {
        transcriptionService.setContentFilteringEnabled(true, 0.6);
      }

      const transcriptionStarted = await transcriptionService.startTranscription(
        `test_${Date.now()}`,
        {
          onTranscript: (segment) => {
            setCurrentTranscript(segment.text);
            setTranscriptError(null);

            // Update full transcript for test as well
            if (segment.isFinal && segment.text.trim()) {
              setFullTranscript(prev => prev + (prev ? ' ' : '') + segment.text.trim());
            }

            onTranscriptionUpdate?.(segment);
          },
          onSessionUpdate: (session) => {
            setTranscriptionSession(session);
          },
          onError: (error) => {
            console.error('Test transcription error:', error);
            toast.error('Transcription Test Failed', `${error}`);
          }
        },
        {
          patientId: 'test-patient',
          sessionType: 'test',
          appointmentReason: 'transcription test'
        }
      );

      if (transcriptionStarted) {
        setIsTranscribing(true);
        toast.info('Transcription Test', 'Test transcription started! Try speaking now. Will auto-stop in 30 seconds.');

        // Auto-stop after 30 seconds
        setTimeout(async () => {
          if (isTranscribing) {
            await transcriptionService.stopTranscription();
            setIsTranscribing(false);
            setCurrentTranscript('');
            setTranscriptError(null);
          }
        }, 30000);
      } else {
        toast.error('Transcription Failed', 'Failed to start test transcription. Check the console for details.');
      }

    } catch (error) {
      console.error('Test transcription failed:', error);
      toast.error('Transcription Failed', `Test transcription failed: ${error}`);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      background: '#1f2937',
      borderRadius: '0.75rem',
      padding: '1.5rem',
      border: '1px solid #374151'
    }}>
      {/* Recording Status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, color: '#f9fafb' }}>Advanced Audio Recording</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} aria-live="polite">
          {isRecording && (
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isPaused ? '#f59e0b' : '#ef4444',
              animation: isPaused ? 'none' : 'pulse 2s infinite'
            }}></div>
          )}
          <span style={{
            color: isRecording ? (isPaused ? '#f59e0b' : '#ef4444') : '#6b7280',
            fontSize: '0.875rem',
            fontWeight: '600'
          }}>
            {isRecording ? (isPaused ? 'PAUSED' : 'RECORDING') : 'READY'}
          </span>
          <span style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            {formatTime(recordingTime)}
          </span>
        </div>
      </div>

      {/* Audio Level Indicator */}
      {isRecording && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            <span style={{ color: '#d1d5db', fontSize: '0.75rem' }}>Audio Level:</span>
            <div style={{
              flex: 1,
              height: '4px',
              background: '#374151',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${audioLevel * 100}%`,
                height: '100%',
                background: audioLevel > 0.8 ? '#ef4444' : audioLevel > 0.5 ? '#f59e0b' : '#D4A017',
                transition: 'width 0.1s ease'
              }}></div>
            </div>
            <span style={{ color: '#d1d5db', fontSize: '0.75rem' }}>
              {Math.round(audioLevel * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Filter Settings */}
      <div style={{ marginBottom: '1rem' }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: '#d1d5db', fontSize: '0.875rem' }}>Audio Filters</h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '0.5rem'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#d1d5db' }}>
            <input
              type="checkbox"
              checked={filterSettings.enableSilenceRemoval}
              onChange={(e) => setFilterSettings(prev => ({ ...prev, enableSilenceRemoval: e.target.checked }))}
              disabled={isRecording}
            />
            <span style={{ fontSize: '0.75rem' }}>Remove Silence</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#d1d5db' }}>
            <input
              type="checkbox"
              checked={filterSettings.enableNoiseReduction}
              onChange={(e) => setFilterSettings(prev => ({ ...prev, enableNoiseReduction: e.target.checked }))}
              disabled={isRecording}
            />
            <span style={{ fontSize: '0.75rem' }}>Noise Reduction</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#d1d5db' }}>
            <input
              type="checkbox"
              checked={filterSettings.enableNormalization}
              onChange={(e) => setFilterSettings(prev => ({ ...prev, enableNormalization: e.target.checked }))}
              disabled={isRecording}
            />
            <span style={{ fontSize: '0.75rem' }}>Normalize Audio</span>
          </label>
        </div>
      </div>

      {/* Recording Controls */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {!isRecording ? (
          <>
            <button
              onClick={startRecording}
              disabled={!isActive}
              className="btn btn-primary"
              style={{
                backgroundColor: '#3b82f6',
                opacity: isActive ? 1 : 0.5,
                cursor: isActive ? 'pointer' : 'not-allowed'
              }}
            >
              🎙️ Start Recording
            </button>
            {enableTranscription && (
              <button
                onClick={testTranscription}
                disabled={!isActive || isTranscribing}
                className="btn btn-secondary"
                style={{
                  opacity: isActive && !isTranscribing ? 1 : 0.5,
                  cursor: isActive && !isTranscribing ? 'pointer' : 'not-allowed'
                }}
              >
                🧪 Test Transcription
              </button>
            )}
          </>
        ) : (
          <>
            <button
              onClick={pauseRecording}
              className="btn btn-secondary"
            >
              {isPaused ? '▶️ Resume' : '⏸️ Pause'}
            </button>
          </>
        )}
      </div>

      {/* Real-time Transcription */}
      {enableTranscription && (isTranscribing || currentTranscript) && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: '#374151',
          borderRadius: '0.5rem',
          border: '1px solid #4b5563'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '0.875rem' }}>
              Live Transcription
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: isTranscribing ? '#D4A017' : '#6b7280',
                animation: isTranscribing ? 'pulse 2s infinite' : 'none'
              }}></div>
              <span style={{
                color: isTranscribing ? '#D4A017' : '#6b7280',
                fontSize: '0.75rem'
              }}>
                {isTranscribing ? 'Active' : 'Stopped'}
              </span>
            </div>
          </div>

          <div style={{
            minHeight: '4rem',
            maxHeight: '12rem',
            overflowY: 'auto',
            padding: '0.75rem',
            background: '#1f2937',
            borderRadius: '0.25rem',
            border: '1px solid #374151',
            scrollBehavior: 'smooth'
          }}>
            <div aria-live="polite" style={{
              margin: 0,
              color: currentTranscript ? '#f9fafb' : '#6b7280',
              fontSize: '0.875rem',
              lineHeight: '1.4',
              fontStyle: currentTranscript ? 'normal' : 'italic',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {(fullTranscript || currentTranscript) || (isTranscribing ? 'Listening for speech...' : 'No transcript available')}
              {transcriptError && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '0.75rem',
                  marginTop: '0.5rem',
                  fontStyle: 'italic'
                }}>
                  Error: {transcriptError}
                </div>
              )}
            </div>
          </div>

          {transcriptionSession && transcriptionSession.segments.length > 0 && (
            <div style={{
              marginTop: '0.5rem',
              display: 'flex',
              gap: '1rem',
              fontSize: '0.75rem',
              color: '#d1d5db',
              flexWrap: 'wrap'
            }}>
              <span>Segments: {transcriptionSession.segments.length}</span>
              <span>Words: {transcriptionSession.totalWords}</span>
              <span>Confidence: {Math.round((transcriptionSession.avgConfidence || 0) * 100)}%</span>
              {transcriptionSession.segments.length > 0 && (
                <span>Last: {new Date(transcriptionSession.segments[transcriptionSession.segments.length - 1].timestamp).toLocaleTimeString()}</span>
              )}
            </div>
          )}

          {/* Show full transcript history */}
          {transcriptionSession && transcriptionSession.segments.length > 1 && (
            <details style={{ marginTop: '0.5rem' }}>
              <summary style={{
                color: '#d1d5db',
                fontSize: '0.75rem',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                View Full Transcript ({transcriptionSession.segments.length} segments)
              </summary>
              <div style={{
                marginTop: '0.5rem',
                maxHeight: '8rem',
                overflowY: 'auto',
                padding: '0.5rem',
                background: '#1f2937',
                borderRadius: '0.25rem',
                fontSize: '0.8rem',
                lineHeight: '1.4'
              }}>
                {transcriptionSession.segments.map((segment, index) => (
                  <div key={segment.id} style={{
                    marginBottom: '0.5rem',
                    paddingBottom: '0.5rem',
                    borderBottom: index < transcriptionSession.segments.length - 1 ? '1px solid #374151' : 'none'
                  }}>
                    <div style={{ color: '#d1d5db', fontSize: '0.7rem', marginBottom: '0.25rem' }}>
                      {new Date(segment.timestamp).toLocaleTimeString()}
                      {segment.speaker && ` - ${segment.speaker}`}
                      {segment.confidence && ` (${Math.round(segment.confidence * 100)}%)`}
                    </div>
                    <div style={{ color: '#f9fafb' }}>
                      {segment.text}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Voice Activity Detection */}
      {enableVAD && vadStats && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: '#374151',
          borderRadius: '0.5rem',
          border: '1px solid #4b5563'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '0.875rem' }}>
              Voice Activity Detection
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: speechActive ? '#D4A017' : '#6b7280',
                animation: speechActive ? 'pulse 2s infinite' : 'none'
              }}></div>
              <span style={{
                color: speechActive ? '#D4A017' : '#6b7280',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                {speechActive ? 'Speech' : 'Silence'}
              </span>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '0.75rem',
            fontSize: '0.75rem'
          }}>
            <div style={{ color: '#d1d5db' }}>
              <div style={{ color: '#d1d5db', fontWeight: '600' }}>Speech Time</div>
              <div>{Math.round(vadStats.totalSpeechTime / 1000)}s</div>
            </div>
            <div style={{ color: '#d1d5db' }}>
              <div style={{ color: '#d1d5db', fontWeight: '600' }}>Speech Ratio</div>
              <div>{Math.round(vadStats.speechRatio)}%</div>
            </div>
            <div style={{ color: '#d1d5db' }}>
              <div style={{ color: '#d1d5db', fontWeight: '600' }}>Segments</div>
              <div>{vadStats.speechSegments}</div>
            </div>
            <div style={{ color: '#d1d5db' }}>
              <div style={{ color: '#d1d5db', fontWeight: '600' }}>Avg Energy</div>
              <div>{Math.round(vadStats.averageEnergyLevel)}dB</div>
            </div>
          </div>
        </div>
      )}

      {/* Content Filtering Status */}
      {enableContentFiltering && filteringStats && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: '#374151',
          borderRadius: '0.5rem',
          border: '1px solid #4b5563'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '0.875rem' }}>
              Content Filtering
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#D4A017'
              }}></div>
              <span style={{ color: '#D4A017', fontSize: '0.75rem' }}>Active</span>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '0.75rem',
            fontSize: '0.75rem'
          }}>
            <div style={{ color: '#d1d5db' }}>
              <div style={{ color: '#d1d5db', fontWeight: '600' }}>Relevant</div>
              <div style={{ color: '#D4A017' }}>{filteringStats.relevantSegments}</div>
            </div>
            <div style={{ color: '#d1d5db' }}>
              <div style={{ color: '#d1d5db', fontWeight: '600' }}>Filtered</div>
              <div style={{ color: '#ef4444' }}>{filteringStats.filteredSegments}</div>
            </div>
            <div style={{ color: '#d1d5db' }}>
              <div style={{ color: '#d1d5db', fontWeight: '600' }}>Medical</div>
              <div style={{ color: '#3b82f6' }}>{filteringStats.medicalContent}</div>
            </div>
            <div style={{ color: '#d1d5db' }}>
              <div style={{ color: '#d1d5db', fontWeight: '600' }}>Avg Score</div>
              <div>{Math.round(filteringStats.averageRelevanceScore * 100)}%</div>
            </div>
          </div>

          {/* Filtering breakdown */}
          {filteringStats.offTopicContent > 0 || filteringStats.interruptionsFiltered > 0 ? (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.5rem',
              background: '#1f2937',
              borderRadius: '0.25rem',
              fontSize: '0.65rem',
              color: '#d1d5db'
            }}>
              {filteringStats.offTopicContent > 0 && (
                <span style={{ marginRight: '1rem' }}>
                  Off-topic: {filteringStats.offTopicContent}
                </span>
              )}
              {filteringStats.interruptionsFiltered > 0 && (
                <span>
                  Interruptions: {filteringStats.interruptionsFiltered}
                </span>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Recording Info */}
      {sessionId && (
        <div style={{
          marginTop: '1rem',
          padding: '0.5rem',
          background: '#374151',
          borderRadius: '0.25rem',
          fontSize: '0.75rem',
          color: '#d1d5db'
        }}>
          Session: {sessionId} {patientId && `| Patient: ${patientId}`}
          {appointmentReason && (
            <div style={{ marginTop: '0.25rem', fontSize: '0.7rem' }}>
              Reason: {appointmentReason}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

AudioRecorder.displayName = 'AudioRecorder';

export default AudioRecorder;